// @spec TSK-STR-046
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { AuditorTaskDetail } from '@/features/auditor-access/components/AuditorTaskDetail';
import { AuditorTaskList } from '@/features/auditor-access/components/AuditorTaskList';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/tasks')({
  component: AuditorTasksPage,
});

/**
 * Auditor tasks page.
 *
 * Displays a read-only list of completed tasks for an organization
 * that the auditor has access to review.
 */
function AuditorTasksPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            {t('routes.goToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: `/auditor/access/${currentAccess.accessId}/tasks` })}
          >
            {t('routes.goToYourTasks')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'tasks'
  if (!currentAccess.accessScope.includes('tasks')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessDeniedTasks')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: '/dashboard' })}
          >
            {t('routes.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
  };

  const handleBackToList = () => {
    setSelectedTaskId(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Auditor mode banner */}
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Page header - only shown in list view */}
          {!selectedTaskId && (
            <header className="space-y-1">
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-amber-600" aria-hidden="true" />
                <h1 className="text-2xl font-semibold text-foreground">{t('routes.auditorTaskReview')}</h1>
              </div>
              <p className="text-muted-foreground">
                {t('routes.readOnlyAccessToTasks', { orgName: currentAccess.name })}
              </p>
            </header>
          )}

          {/* Content */}
          {selectedTaskId ? (
            <AuditorTaskDetail
              accessId={accessId}
              taskId={selectedTaskId}
              onBack={handleBackToList}
            />
          ) : (
            <AuditorTaskList
              accessId={accessId}
              organizationName={currentAccess.name}
              onTaskClick={handleTaskClick}
            />
          )}
        </div>
      </div>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}
